import type { Subscription } from '@skop-app/lib-domain/types'

export type SubscriptionPlanType = 'FREE' | 'PRO' | 'ORGANIZATION' | 'CUSTOM'
export type BillingInterval = 'monthly' | 'annual'
export type SubscriptionBillingInterval = BillingInterval

export type SubscriptionCheckoutLookupKey =
  | 'BUSINESS'
  | 'BUSINESS_ANNUAL'
  | 'EXPERT'
  | 'EXPERT_ANNUAL'

export type ManageSubscription = Subscription & {
  quotaCustom?: {
    products?: number | null
    projects?: number | null
    quoteSignatures?: number | null
    subTeams?: number | null
  }
}

export type SubscriptionPlan = {
  id: number
  nameKey: string
  priceMonthly: number
  priceAnnual: number
  teamMembers: number
  products: number
  projects: number
  quoteSignatures?: number
  subTeams?: number
  type: SubscriptionPlanType
  billingModel?: 'fixed' | 'per-seat'
  billingInterval?: BillingInterval
}

export const isSeatConsumingMember = (member: {
  projectID?: string | null
  type?: string | null
  status?: string | null
  onSeat?: boolean | null
}): boolean =>
  !member.projectID?.trim() &&
  member.type?.trim() !== 'PROJECT' &&
  member.status?.trim() === 'DONE' &&
  member.onSeat !== false

// Legacy fixed-price plans and Stripe price lookup keys resolve to the
// entitlement plan types used by skop-data. User-facing names live in Plan.*.
export const normalizeSubscriptionPlanType = (
  planType?: string | null,
): SubscriptionPlanType | undefined => {
  if (planType === 'FREE' || planType === 'CUSTOM') return planType
  if (
    planType === 'PRO' ||
    planType === 'BUSINESS' ||
    planType === 'BUSINESS_ANNUAL'
  )
    return 'PRO'
  if (
    planType === 'ORGANIZATION' ||
    planType === 'CORPORATE' ||
    planType === 'EXPERT' ||
    planType === 'EXPERT_ANNUAL'
  )
    return 'ORGANIZATION'
  return undefined
}

export const isActiveCyneoSubscription = (
  subscription?: {
    stripeID?: string | null
    status?: string | null
  } | null,
): boolean =>
  subscription?.stripeID === 'CYNEO' &&
  (subscription.status === 'ACTIVE' || subscription.status === 'TRIALING')

export const isActiveFixedPriceSubscription = (
  subscription?: { type?: string | null; status?: string | null } | null,
): boolean =>
  (subscription?.status === 'ACTIVE' ||
    subscription?.status === 'TRIALING' ||
    subscription?.status === 'PAST_DUE' ||
    subscription?.status === 'UNPAID' ||
    subscription?.status === 'PAUSED' ||
    subscription?.status === 'INCOMPLETE') &&
  (subscription.type === 'PRO' ||
    subscription.type === 'ORGANIZATION' ||
    subscription.type === 'CORPORATE')

export const subscriptionsPlans: SubscriptionPlan[] = [
  {
    id: 1,
    nameKey: 'Plan.discovery',
    priceMonthly: 0,
    priceAnnual: 0,
    teamMembers: 1,
    products: 200,
    projects: 1,
    type: 'FREE',
    billingModel: 'fixed',
  },
  {
    id: 2,
    nameKey: 'Plan.pro',
    priceMonthly: 24,
    priceAnnual: 240,
    teamMembers: 3,
    products: 1200,
    projects: 5,
    type: 'PRO',
    billingModel: 'per-seat',
  },
  {
    id: 3,
    nameKey: 'Plan.expert',
    priceMonthly: 39,
    priceAnnual: 390,
    teamMembers: 10,
    products: 5000,
    projects: 20,
    type: 'ORGANIZATION',
    billingModel: 'per-seat',
  },
]

export const buildSubscriptionPlans = (
  currentSubscription?: ManageSubscription,
): SubscriptionPlan[] => {
  const currentPlanType = normalizeSubscriptionPlanType(
    currentSubscription?.type,
  )
  if (!currentSubscription || currentPlanType !== 'CUSTOM') {
    return subscriptionsPlans.map((plan) => {
      if (plan.type !== currentPlanType) return plan
      const currentType = currentSubscription?.type as string | undefined
      const legacyFixed = isActiveFixedPriceSubscription(currentSubscription)
      return {
        ...plan,
        teamMembers: currentSubscription?.seatCount ?? plan.teamMembers,
        billingInterval:
          currentType === 'BUSINESS_ANNUAL' || currentType === 'EXPERT_ANNUAL'
            ? 'annual'
            : 'monthly',
        ...(legacyFixed
          ? {
              priceMonthly: currentType === 'PRO' ? 85 : 380,
              billingModel: 'fixed' as const,
            }
          : { billingModel: plan.billingModel }),
      }
    })
  }

  return [
    {
      id: 4,
      nameKey: 'Plan.custom',
      priceMonthly: 0,
      priceAnnual: 0,
      teamMembers: currentSubscription.seatCount ?? Number.NaN,
      products: currentSubscription.quotaCustom?.products ?? Number.NaN,
      projects: currentSubscription.quotaCustom?.projects ?? Number.NaN,
      quoteSignatures:
        currentSubscription.quotaCustom?.quoteSignatures ?? Number.NaN,
      subTeams: currentSubscription.quotaCustom?.subTeams ?? Number.NaN,
      type: 'CUSTOM',
    },
    ...subscriptionsPlans,
  ]
}

/**
 * Default seat count per plan (FREE=1, PRO/BUSINESS=3, ORGANIZATION/EXPERT=10),
 * overridden by an explicit `seatCount` on the subscription when present.
 */
export const resolveSubscriptionSeatLimit = (
  subscription?: {
    type?: string | null
    seatCount?: number | null
  } | null,
): number | null => {
  if (Number.isInteger(subscription?.seatCount)) {
    return subscription!.seatCount as number
  }

  const normalizedType =
    normalizeSubscriptionPlanType(subscription?.type) ?? 'FREE'
  const plan = subscriptionsPlans.find((item) => item.type === normalizedType)
  return plan?.teamMembers ?? null
}

export const resolveSubscriptionCheckoutLookupKey = (
  planType: SubscriptionPlanType,
  interval: BillingInterval = 'monthly',
): SubscriptionCheckoutLookupKey | undefined => {
  if (planType === 'PRO')
    return interval === 'annual' ? 'BUSINESS_ANNUAL' : 'BUSINESS'
  if (planType === 'ORGANIZATION')
    return interval === 'annual' ? 'EXPERT_ANNUAL' : 'EXPERT'
  return undefined
}

export const resolveSubscriptionBillingIntervalFromType = (
  type: string | null | undefined,
): BillingInterval => {
  if (type === 'BUSINESS_ANNUAL' || type === 'EXPERT_ANNUAL') {
    return 'annual'
  }
  return 'monthly'
}

export const getSubscriptionPlanPrice = (
  plan: Pick<SubscriptionPlan, 'priceMonthly' | 'priceAnnual'>,
  billingInterval: BillingInterval,
): number =>
  billingInterval === 'annual' ? plan.priceAnnual : plan.priceMonthly
